import { ChevronDown, RotateCcw } from 'lucide-react'
import IndicatorConfigModal from '@/components/Indicators/IndicatorConfigModal'
import ColorPickerField from '@/components/Indicators/ColorPickerField'
import SliderField from '@/components/Indicators/SliderField'
import ToggleField from '@/components/Indicators/ToggleField'
import { higherTimeframes, sanitizeHtfTimeframes, mtfLabel, MTF_MAX_ADDITIONAL } from '@/utils/mtf'
import { TIMEFRAMES } from '@/constants/timeframes'
import { defaultConfig } from './index'

function Section({ title, children, sectionKey, config, onChange }) {
  const open = config?.panelSections?.[sectionKey] ?? false
  const toggle = () => onChange({ ...config, panelSections: { ...(config.panelSections || {}), [sectionKey]: !open } })
  return (
    <div className="border border-[var(--hair,rgba(176,141,87,0.35))] rounded-lg overflow-hidden">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between px-2.5 py-2 bg-[var(--paper,#FDFDFA)] hover:bg-black/[0.02] transition-colors"
      >
        <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold,#B08D57)]">{title}</span>
        <ChevronDown size={14} className={`text-[var(--ink-soft,#4A5A52)] transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && (
        <div className="px-2.5 py-2 space-y-2 bg-white/50 dark:bg-gray-900/20">
          {children}
        </div>
      )}
    </div>
  )
}

const STYLE_KEYS = [
  'colorBull', 'colorBear',
]

export default function CHoCHBOSQuantumPanel({ config, onChange, onClose, chartTimeframe }) {
  const cfg = config || {}

  const update = (key, value) => onChange({ ...cfg, [key]: value })
  const updateSection = (section, key, value) => {
    onChange({ ...cfg, [section]: { ...(cfg[section] || {}), [key]: value } })
  }

  // MTF: solo se ofrecen TFs estrictamente mayores que el del chart; la
  // selección se sanea contra el chart actual (un TF que deja de ser mayor
  // desaparece de los chips y deja de pedirse, sin borrar la config).
  const htfOptions = higherTimeframes(chartTimeframe)
  const htfSelected = sanitizeHtfTimeframes(cfg.htfTimeframes ?? [], chartTimeframe)
  const chartTfLabel = TIMEFRAMES.find((t) => t.value === chartTimeframe)?.label ?? chartTimeframe
  const toggleHtf = (tf) => {
    if (htfSelected.includes(tf)) {
      update('htfTimeframes', htfSelected.filter((t) => t !== tf))
    } else if (htfSelected.length < MTF_MAX_ADDITIONAL) {
      update('htfTimeframes', [...htfSelected, tf])
    }
  }

  const resetStyle = () => {
    const patch = {}
    for (const k of STYLE_KEYS) patch[k] = defaultConfig[k]
    onChange({ ...cfg, ...patch })
  }

  return (
    <IndicatorConfigModal title="Configuración CHoCH / BOS Quantum" isOpen onClose={onClose}>
      <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">

        <Section title="Lógica" sectionKey="logic" config={cfg} onChange={onChange}>
          <SliderField label="Swing pivot length" value={cfg.structure?.swingLen ?? 10} min={3} max={50} step={1} unit=" barras" onChange={(v) => updateSection('structure', 'swingLen', v)} />
          <SliderField label="Internal pivot length" value={cfg.structure?.internalLen ?? 5} min={2} max={20} step={1} unit=" barras" onChange={(v) => updateSection('structure', 'internalLen', v)} />
          <SliderField label="Mín. mov. ATR" value={cfg.structure?.minMoveATRMult ?? 0.5} min={0} max={2} step={0.1} onChange={(v) => updateSection('structure', 'minMoveATRMult', v)} />
          <SliderField label="Máx. eventos swing" value={cfg.structure?.maxHistoryEvents ?? 5} min={1} max={20} step={1} onChange={(v) => updateSection('structure', 'maxHistoryEvents', v)} />
          <SliderField label="Máx. eventos internos" value={cfg.structure?.maxInternalEvents ?? 3} min={1} max={20} step={1} onChange={(v) => updateSection('structure', 'maxInternalEvents', v)} />
          <div className="space-y-1" data-testid="choch-bos-mtf-section">
            <span className="text-xs text-[var(--ink-soft,#4A5A52)]">Timeframes adicionales</span>
            {htfOptions.length === 0 ? (
              <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
                No hay timeframes superiores disponibles desde {chartTfLabel}.
              </p>
            ) : (
              <>
                <div className="grid grid-cols-4 gap-1">
                  {htfOptions.map((tf) => {
                    const selected = htfSelected.includes(tf)
                    const disabled = !selected && htfSelected.length >= MTF_MAX_ADDITIONAL
                    return (
                      <button
                        key={tf}
                        type="button"
                        disabled={disabled}
                        onClick={() => toggleHtf(tf)}
                        aria-pressed={selected}
                        data-tf={tf}
                        className={`px-1 py-1 text-[10px] font-medium rounded border transition-colors ${
                          selected
                            ? 'bg-[var(--paper,#FDFDFA)] border-[var(--gold,#B08D57)] text-[var(--green-deep,#0B3D2E)]'
                            : 'bg-white dark:bg-gray-800 border-slate-200 dark:border-gray-700 text-slate-600 dark:text-gray-400 hover:bg-slate-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:hover:bg-white'
                        }`}
                      >
                        {mtfLabel(tf)}
                      </button>
                    )
                  })}
                </div>
                <p className="text-[10px] text-[var(--ink-soft,#4A5A52)] leading-relaxed">
                  CHoCH/BOS de timeframes mayores: línea punteada y etiqueta con sufijo (p. ej. BOS · 4H). Su ciclo de vida se evalúa en su propio timeframe. Máx. {MTF_MAX_ADDITIONAL}.
                </p>
              </>
            )}
          </div>
        </Section>

        <Section title="Visuales" sectionKey="visual" config={cfg} onChange={onChange}>
          <ToggleField label="Swing BOS / CHoCH" value={cfg.structure?.showSwingStruct ?? true} onChange={(v) => updateSection('structure', 'showSwingStruct', v)} />
          <ToggleField label="Estructura interna" value={cfg.structure?.showInternalStruct ?? true} onChange={(v) => updateSection('structure', 'showInternalStruct', v)} />
          <ToggleField label="Etiquetas de eventos" value={cfg.structure?.showSwingLabels ?? true} onChange={(v) => updateSection('structure', 'showSwingLabels', v)} />
        </Section>

        <Section title="Colores" sectionKey="colors" config={cfg} onChange={onChange}>
          <ColorPickerField label="BOS / CHoCH alcista" value={cfg.colorBull ?? '#089981'} onChange={(v) => update('colorBull', v)} />
          <ColorPickerField label="BOS / CHoCH bajista" value={cfg.colorBear ?? '#f23645'} onChange={(v) => update('colorBear', v)} />
        </Section>

        <Section title="Estilo avanzado" sectionKey="style" config={cfg} onChange={onChange}>
          <button
            onClick={resetStyle}
            className="w-full flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium rounded border border-[var(--hair,rgba(176,141,87,0.35))] bg-[var(--paper,#FDFDFA)] text-[var(--green-deep,#0B3D2E)] hover:bg-[var(--gold,#B08D57)] hover:text-white transition-colors"
          >
            <RotateCcw size={12} />
            Restablecer estilo Quantum
          </button>
        </Section>

      </div>
    </IndicatorConfigModal>
  )
}
